import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchPanchangCity } from "@/lib/api";
import Seo from "@/components/Seo";
import { T } from "@/components/T";
import Panchanga from "@/pages/Panchang";
import { RotateCcw, ArrowLeft } from "lucide-react";

// SEO-indexed, fixed-location daily Panchangam for a city. Everything computes
// LIVE from the Jyotish engine in the city's own timezone (auto year rollover).
// Choghadiya & the scriptural almanac stay premium-only (inherited from Panchanga).
export default function CityPanchang() {
  const { city: slug } = useParams();
  const [city, setCity] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | error

  useEffect(() => {
    let active = true;
    setState("loading");
    fetchPanchangCity(slug)
      .then((c) => { if (active) { setCity(c); setState("ok"); } })
      .catch(() => { if (active) setState("error"); });
    return () => { active = false; };
  }, [slug]);

  if (state === "loading") {
    return <div data-testid="city-panchang-loading" className="mx-auto max-w-5xl px-5 py-24 text-center text-ink-3"><T>Loading…</T></div>;
  }
  if (state === "error" || !city) {
    return (
      <div data-testid="city-panchang-error" className="mx-auto max-w-5xl px-5 py-24 text-center">
        <p className="text-ink-2"><T>This city Panchangam page is not available.</T></p>
        <div className="mt-6 flex items-center justify-center gap-3">
          <button onClick={() => window.location.reload()} className="inline-flex items-center gap-2 rounded-full border border-saffron bg-[rgba(199,80,0,0.06)] px-5 py-2.5 text-sm font-medium text-saffron hover:bg-saffron hover:text-white">
            <RotateCcw className="h-4 w-4" /> <T>Retry</T>
          </button>
          <Link to="/panchang" className="inline-flex items-center gap-2 rounded-full border border-line px-5 py-2.5 text-sm text-ink-2 hover:text-ink">
            <ArrowLeft className="h-4 w-4" /> <T>Daily Panchangam</T>
          </Link>
        </div>
      </div>
    );
  }

  const place = `${city.name}${city.region ? ", " + city.region : ""}${city.country ? ", " + city.country : ""}`;
  const title = `${city.name} Panchangam — Today's Tithi, Nakshatra, Rahu Kalam & Muhurta`;
  const description = `Daily Panchangam for ${place}: today's tithi, nakshatra, yoga, karana, vara, sunrise & sunset, Rahu Kalam, Yamaganda, Gulika and auspicious muhurta — computed live in ${city.name}'s local time.`;

  return (
    <div data-testid="city-panchang-page">
      <Seo
        path={`/panchangam/${slug}`}
        title={title}
        description={description}
        keywords={`${city.name} panchang, ${city.name} panchangam, today tithi ${city.name}, ${city.name} rahu kalam, ${city.name} nakshatra, ${city.name} muhurta`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: title,
          description,
          about: { "@type": "Place", name: place },
        }}
      />
      <Panchanga city={city} />
    </div>
  );
}
